import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Popconfirm, Space, message, InputNumber } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";

// Editable numeric field for organizzazione.limite_servizi (stored as string)
const FieldLimiteServizi = ({ org }) => {
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();

  const parseInitial = useCallback(() => {
    const raw = org?.limite_servizi;
    if (raw === null || typeof raw === "undefined") return null;
    const n = Number(String(raw));
    return Number.isFinite(n) ? n : null;
  }, [org?.limite_servizi]);

  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(parseInitial());
  const [originalValue, setOriginalValue] = useState(parseInitial());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const displayValue = useMemo(() => {
    if (value === null || typeof value === "undefined") return "-";
    try {
      return String(value);
    } catch (_) {
      return String(value);
    }
  }, [value]);

  const handleConfirm = async () => {
    if (value === null || typeof value === "undefined" || !Number.isFinite(value)) {
      setConfirmOpen(false);
      setEditing(false);
      return;
    }
    try {
      setSaving(true);
      const token = await getTokenSilently();
      const graphqlUrl = `${config.baseUrl[isProd ? "prod" : "dev"]}/graphql?raw=true`;
      const mutation = `
        mutation UpdateOrganizzazione($id: ID!, $fields: UpdateOrganizzazioneFieldsInput) {
          updateOrganizzazione(input: { id: $id, fields: $fields }) {
            organizzazione { objectId limite_servizi }
          }
        }
      `.replace(/\r?\n|\r/g, "").replace(/\t/g, " ");

      const body = {
        query: mutation,
        variables: { id: org.objectId, fields: { limite_servizi: String(value) } },
      };

      const resp = await fetch(graphqlUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const json = await resp.json();
      if (json.errors) throw new Error(json.errors[0]?.message || "GraphQL error");

      message.success("Fatto");
      setOriginalValue(value);
      setConfirmOpen(false);
      setEditing(false);
    } catch (e) {
      console.error("Errore aggiornamento limite_servizi:", e);
      message.error("Si è verificato un problema: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelAll = () => {
    setValue(originalValue);
    setConfirmOpen(false);
    setEditing(false);
  };

  useEffect(() => {
    const next = parseInitial();
    setValue(next);
    setOriginalValue(next);
  }, [parseInitial]);

  return (
    <>
      {!editing && (
        <Space size="small">
          <span>{displayValue}</span>
          <Button size="small" type="link" icon={<EditOutlined />} onClick={() => setEditing(true)}>
            Modifica
          </Button>
        </Space>
      )}
      {editing && (
        <Space size="small">
          <Popconfirm
            title="Sei sicuro di voler aggiornare la org?"
            open={confirmOpen}
            okButtonProps={{ loading: saving }}
            onConfirm={handleConfirm}
            onCancel={() => { setConfirmOpen(false); setEditing(false); }}
          >
            <InputNumber
              min={0}
              value={value}
              onChange={(v) => { if (typeof v === "number") { setValue(v); setConfirmOpen(true); } }}
            />
          </Popconfirm>
          <Button size="small" onClick={handleCancelAll}>Annulla</Button>
        </Space>
      )}
    </>
  );
};

export default FieldLimiteServizi;
